/**
 * The public API request/response shapes (TECHNICAL_PLAN.md §7).
 *
 * CONTRACT-FIRST: the client builds against these. Once they are coding, additive
 * changes only — anything breaking becomes /v2. See PROJECT_BRIEF.md §9.
 */
import { z } from "zod";
import { paginationQuerySchema } from "./pagination.js";
import { PAYMENT_METHODS } from "./vocabulary.js";
import { isDecimal2 } from "./money.js";
import { isValidOib } from "./oib.js";
/**
 * Which set of message rules a sale follows: spec §2.2 (a self-service device)
 * or §2.1 (a receipt issued by an operator). `unattended` is the default
 * everywhere, so nothing already integrated changes.
 */
export const SALE_TYPES = ["unattended", "attended"];
const decimal2 = z
    .string()
    .refine(isDecimal2, { message: 'Must be a decimal with exactly 2 places, e.g. "12.00"' });
export const taxLineSchema = z.object({
    rate: decimal2,
    base: decimal2,
    amount: decimal2,
});
/**
 * `OstaliPor/Porez` — "other taxes" carry a name, and it is mandatory once the
 * group is present (§2.1.1). Optional here, defaulted downstream, so an
 * existing caller sending an unnamed line still gets a schema-valid message.
 */
export const namedTaxLineSchema = taxLineSchema.extend({
    name: z.string().min(1).max(100).optional(),
});
/**
 * A `Char(11)` OIB as the XSD sees it — eleven digits, nothing more.
 *
 * The ISO 7064 checksum is deliberately NOT enforced here, because the service
 * does not enforce it at the schema level either: a bad check digit on the
 * recipient OIB comes back as restrictive error **180**, not as `s001`. The HR
 * validator raises exactly that, with that upstream code attached, so the error
 * a caller sees matches the error the Tax Administration would have sent.
 */
const oib11 = z.string().regex(/^[0-9]{11}$/, "An OIB is exactly 11 digits");
/**
 * POST /v1/invoices.
 *
 * ## The field set
 *
 * Every element of `RacunZahtjev` that is not fleet identity is accepted here
 * (`TODO.md` item 5). `Oib` and `OznPosPr` are not: they come from the resolved
 * company and location, because the OIB must match the signing certificate
 * (`s005`) and the premises code is part of the invoice's uniqueness key.
 *
 * ## Omitted vs. explicitly none
 *
 * Optional fields are `.nullable()`, and the two states are NOT the same:
 *
 *   - **absent** — fall back through `devices.device_config`, then
 *     `companies.fiscal_config`, then the spec default.
 *   - **`null`** — explicitly none. The element is omitted and no configured
 *     default is consulted.
 *
 * Without the distinction a device-level default silently reappears on the one
 * sale that meant to clear it.
 *
 * ## Compatibility
 *
 * Additive only — the client builds against this schema (`PROJECT_BRIEF.md` §9).
 * Everything new is optional; every previously required field is still
 * required; `payment_method` widened from `G|K|O` to include `T`, which accepts
 * strictly more requests than before. `T` is still refused for an unattended
 * sale, now by the HR validator with a message naming `sale_type`.
 */
export const createInvoiceSchema = z.object({
    device_id: z.string().uuid(),
    /** ISO 8601. The SALE moment, not the send time. */
    occurred_at: z.string().datetime({ offset: true }),
    total: decimal2,
    /**
     * HR fiscalization is denominated in EUR by law and the validator rejects
     * anything else, so this keeps its literal default rather than joining the
     * configuration chain — there is nothing for a tenant to configure.
     */
    currency: z.string().length(3).default("EUR"),
    payment_method: z.enum(PAYMENT_METHODS),
    /**
     * `unattended` (spec §2.2, a self-service device) or `attended` (§2.1, a
     * receipt issued by an operator). Defaults to `unattended`, so a caller that
     * has never sent this field keeps exactly the behaviour it had.
     */
    sale_type: z.enum(SALE_TYPES).default("unattended"),
    vat: z.array(taxLineSchema).nullable().optional(),
    consumption_tax: z.array(taxLineSchema).nullable().optional(),
    other_taxes: z.array(namedTaxLineSchema).nullable().optional(),
    vat_exempt_amount: decimal2.nullable().optional(),
    margin_amount: decimal2.nullable().optional(),
    non_taxable_amount: decimal2.nullable().optional(),
    fees: z
        .array(z.object({ name: z.string().min(1).max(100), amount: decimal2 }))
        .nullable()
        .optional(),
    /** `SpecNamj`, Varchar(1000). */
    special_purpose: z.string().max(1000).nullable().optional(),
    /** `NakDost`. Set automatically on every re-send; supplying it forces it true. */
    late_delivery: z.boolean().nullable().optional(),
    /** `USustPdv`. Defaults to the company's registration flag. */
    vat_registered: z.boolean().nullable().optional(),
    /* --- attended receipts (§2.1) ------------------------------------------- */
    /** `OznSlijed` — `P` per business premises, `N` per device. Attended only. */
    sequence_mark: z.enum(["P", "N"]).nullable().optional(),
    /**
     * `BrRac/OznNapUr` for an attended receipt: the cash register's mark, which
     * is NOT an ISU number. An unattended sale always reports its device's ISU
     * number and rejects this field.
     */
    register_mark: z
        .string()
        .regex(/^[1-9][0-9]{0,19}$/, "A register mark is digits with no leading zeros")
        .nullable()
        .optional(),
    /** `OibOper` — the operator's OIB. Unattended sales report the company's. */
    operator_oib: oib11.nullable().optional(),
    /**
     * `OibPrimateljaRacuna` — B2B only, and never together with `payment_method`
     * `T` (restrictive error 181). Not part of the unattended message at all.
     */
    recipient_oib: oib11.nullable().optional(),
    /** `ParagonBrRac` — hand-written receipts copied up after a device failure. */
    paragon_number: z.string().max(100).nullable().optional(),
});
export const oibSchema = z
    .string()
    .refine(isValidOib, { message: "Invalid OIB (11 digits, ISO 7064 MOD 11,10 checksum)" });
export const createCompanySchema = z.object({
    name: z.string().min(1).max(200),
    oib: oibSchema,
    /** Selects the fiscalization provider. A taxpayer files in exactly one country. */
    country_code: z.string().length(2).default("HR"),
    vat_registered: z.boolean().default(true),
    address: z.record(z.unknown()).default({}),
    fiscal_config: z.record(z.unknown()).default({}),
    mode: z.enum(["test", "live"]).default("test"),
});
export const createDeviceSchema = z.object({
    company_id: z.string().uuid(),
    /**
     * `<OznPosPr>` — spec §2.2.1: 0-9, a-z, A-Z only, max 20.
     *
     * Registered in ePorezna, never here. It used to live on a separate
     * `locations` resource; that resource held one field that reached a fiscal
     * message and a form of premises master data this product cannot register,
     * so it was folded into the device (db/migrations/0006).
     */
    premises_code: z.string().regex(/^[0-9a-zA-Z]{1,20}$/),
    /** Free text, for the dashboard. Never sent to the Tax Administration. */
    premises_address: z.string().max(500).nullable().optional(),
    label: z.string().min(1).max(200),
    /** Assigned by the Tax Administration via ePorezna. We can never generate it (§5.7). */
    isu_number: z
        .string()
        .regex(/^[1-9][0-9]{0,19}$/, "ISU number must be digits with no leading zeros")
        .optional(),
    device_config: z.record(z.unknown()).default({}),
});
export const invoiceListQuerySchema = paginationQuerySchema.extend({
    company_id: z.string().uuid().optional(),
    device_id: z.string().uuid().optional(),
    premises_code: z.string().max(20).optional(),
    status: z.enum(["pending", "confirmed", "retrying", "failed", "expired"]).optional(),
    from: z.string().datetime({ offset: true }).optional(),
    to: z.string().datetime({ offset: true }).optional(),
    /** Free text over the receipt number, the JIR and the ZKI. */
    q: z.string().min(1).max(64).optional(),
});
//# sourceMappingURL=dto.js.map